import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface ConstructionSite {
  id: string;
  name: string;
  address: string | null;
}

export interface ChatProject {
  id: string;
  name: string;
  construction_site_id: string | null;
  engine_id: string | null;
  created_at: string;
  updated_at: string;
}

export function useProjects() {
  const { user } = useAuth();
  const [sites, setSites] = useState<ConstructionSite[]>([]);
  const [chats, setChats] = useState<ChatProject[]>([]);
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async () => {
    if (!user) { setSites([]); setChats([]); return; }
    setLoading(true);
    const [sitesRes, chatsRes] = await Promise.all([
      supabase.from("construction_sites").select("id, name, address").eq("user_id", user.id).order("created_at"),
      supabase.from("projects").select("id, name, construction_site_id, engine_id, created_at, updated_at").eq("user_id", user.id).order("updated_at", { ascending: false }),
    ]);
    if (sitesRes.data) setSites(sitesRes.data);
    if (chatsRes.data) setChats(chatsRes.data);
    setLoading(false);
  }, [user]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const createChat = useCallback(async (name: string, siteId?: string | null, engineId?: string | null): Promise<string | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from("projects")
      .insert({ user_id: user.id, name, construction_site_id: siteId || null, engine_id: engineId || null })
      .select("id")
      .single();
    if (error || !data) return null;
    await loadAll();
    return data.id;
  }, [user, loadAll]);

  const deleteChat = useCallback(async (chatId: string) => {
    await supabase.from("chat_messages").delete().eq("project_id", chatId);
    await supabase.from("projects").delete().eq("id", chatId);
    await loadAll();
  }, [loadAll]);

  const assignChat = useCallback(async (chatId: string, siteId: string | null) => {
    await supabase.from("projects").update({ construction_site_id: siteId }).eq("id", chatId);
    await loadAll();
  }, [loadAll]);

  const renameChat = useCallback(async (chatId: string, name: string) => {
    await supabase.from("projects").update({ name }).eq("id", chatId);
    await loadAll();
  }, [loadAll]);

  const saveChatMessage = useCallback(async (projectId: string, role: string, content: string): Promise<string | null> => {
    const { data, error } = await supabase
      .from("chat_messages")
      .insert({ project_id: projectId, role, content })
      .select("id")
      .single();
    if (error || !data) return null;
    return data.id;
  }, []);

  const loadChatMessages = useCallback(async (projectId: string) => {
    const { data } = await supabase
      .from("chat_messages")
      .select("id, role, content, created_at")
      .eq("project_id", projectId)
      .order("created_at");
    return data || [];
  }, []);

  const addSite = useCallback(async (name: string, address?: string) => {
    if (!user) return;
    await supabase.from("construction_sites").insert({ user_id: user.id, name, address: address || null });
    await loadAll();
  }, [user, loadAll]);

  return { sites, chats, loading, createChat, deleteChat, assignChat, renameChat, saveChatMessage, loadChatMessages, addSite, reload: loadAll };
}
